import { useState } from 'react';
import Dexie from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  CUENTAS_BANCO,
  DEFAULT_SETTINGS,
  FX_SCALE,
  centavos,
  esCuentaEnDolares,
  formatFxRate,
  formatMoney,
  fxRate,
  hoy,
  newId,
  parseMoney,
  type Centavos,
  type CuentaBanco,
  type CuentaBancoCordobas,
  type CuentaBancoDolares,
  type Settings,
} from '@abastece/shared';
import { ACCOUNTS } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaDeTransferencias, cuentasAbiertas, nombreDeCuenta, nombrePuesto, primeraEnDolares, saldosDeBancos } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import { Campo, Selector } from './Campo.js';
import { Dato, enDolares } from './CuentasDeCaja.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * «Mover dinero»: cash between general cash and the bank, either way (the owner, 2026-10-07: «in
 * the accounting page we need a way to transfer cash from general cash to bank and vice versa»),
 * and money between the shop's bank accounts (2026-10-10).
 *
 * To the bank it is a deposit, the same as «Cuadrar la semana» writes down (`efectivo.depositado`);
 * from the bank, the same the other way (`efectivo.sacado_del_banco`). Córdobas go to or come from
 * a córdoba account and dollars a dollar account, on the slip's day: which one is asked only when
 * the shop has more than one (`ElegirCuenta`). Between accounts it is `banco.transferido`, and one
 * that changes currency says what arrived too. All of it is listed, and voided, in «Efectivo». What
 * each account holds by the books is shown, and not held against: the safe is only as right as its
 * last count, and the bank as its last statement.
 */

export type Sentido = 'alBanco' | 'delBanco' | 'entreCuentas';

/** An amount as typed: nothing typed is none, anything else must be an amount. */
const leer = (texto: string): Centavos | null => (texto.trim() === '' ? centavos(0) : parseMoney(texto));

/** An account's balance by the books: its days added up (`SaldoDiario`). */
async function saldoDe(codigo: string): Promise<Centavos> {
  let s = 0;
  await db.saldosDiarios
    .where('[cuenta+fecha]')
    .between([codigo, Dexie.minKey], [codigo, Dexie.maxKey])
    .each((d) => {
      s += d.debe - d.haber;
    });
  return centavos(s);
}

/** The picked account while it is still open in its currency, or the one it starts on. */
function abiertaO<C extends CuentaBanco>(config: Settings, moneda: 'NIO' | 'USD', elegida: C | null, porDefecto: C): C {
  return cuentasAbiertas(config, moneda).some((c) => c.cuenta === elegida) ? elegida! : porDefecto;
}

export function MoverDinero({ onCerrar }: { onCerrar: () => void }) {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [sentido, setSentido] = useState<Sentido>('alBanco');
  const [cordobas, setCordobas] = useState('');
  const [dolares, setDolares] = useState('');
  const [elegidaC, setElegidaC] = useState<CuentaBancoCordobas | null>(null);
  const [elegidaD, setElegidaD] = useState<CuentaBancoDolares | null>(null);
  const [elegidaDesde, setElegidaDesde] = useState<CuentaBanco | null>(null);
  const [elegidaHacia, setElegidaHacia] = useState<CuentaBanco | null>(null);
  const [monto, setMonto] = useState('');
  const [recibido, setRecibido] = useState('');
  const [referencia, setReferencia] = useState('');
  const [nota, setNota] = useState('');
  const [dia, setDia] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saldos = useLiveQuery(async () => ({
    efectivo: await saldoDe(ACCOUNTS.CAJA_FUERTE.code),
    bancos: await saldosDeBancos(CUENTAS_BANCO),
  }));

  const fecha = dia || hoy();
  const fechaValida = fecha <= hoy();
  const abiertas = cuentasAbiertas(config);
  const nombre = (cuenta: string) => nombreDeCuenta(config, cuenta, t);

  // Cash to or from the bank.
  const c = leer(cordobas);
  const d = leer(dolares);
  const cuentaC = abiertaO(config, 'NIO', elegidaC, cuentaDeTransferencias(config));
  const cuentaD = abiertaO(config, 'USD', elegidaD, primeraEnDolares(config));
  const hayVariasC = cuentasAbiertas(config, 'NIO').length > 1;
  const hayVariasD = cuentasAbiertas(config, 'USD').length > 1;

  // Between accounts: from one, to any other.
  const desde = abiertas.some((x) => x.cuenta === elegidaDesde) ? elegidaDesde! : cuentaDeTransferencias(config);
  const destinos = abiertas.filter((x) => x.cuenta !== desde);
  const hacia = destinos.some((x) => x.cuenta === elegidaHacia) ? elegidaHacia! : (destinos[0]?.cuenta ?? null);
  const cambia = hacia !== null && esCuentaEnDolares(desde) !== esCuentaEnDolares(hacia);
  const m = leer(monto);
  const r = leer(recibido);
  /** What the bank changed them at, when the money changed currency: córdobas for each dollar. */
  const tasaDelBanco =
    cambia && m !== null && r !== null && m > 0 && r > 0
      ? esCuentaEnDolares(desde)
        ? Math.round((r * FX_SCALE) / m)
        : Math.round((m * FX_SCALE) / r)
      : null;

  const alBanco = sentido === 'alBanco';
  const entre = sentido === 'entreCuentas';
  const valido = entre
    ? hacia !== null && m !== null && m > 0 && (!cambia || (r !== null && r > 0)) && fechaValida
    : c !== null && c >= 0 && d !== null && d >= 0 && c + d > 0 && fechaValida;

  async function guardar() {
    if (!valido || guardando) return;
    setGuardando(true);
    setError(null);
    const extras = {
      ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
      ...(nota.trim() ? { nota: nota.trim() } : {}),
    };
    try {
      const { rechazo } = entre
        ? await emitirYVer('banco.transferido', {
            transferenciaId: newId(),
            fecha,
            desde,
            hacia: hacia!,
            monto: m!,
            ...(cambia ? { recibido: r! } : {}),
            ...extras,
          })
        : await emitirYVer(alBanco ? 'efectivo.depositado' : 'efectivo.sacado_del_banco', {
            depositoId: newId(),
            fecha,
            cordobas: c!,
            dolares: d!,
            ...(c! > 0 ? { cuenta: cuentaC } : {}),
            ...(d! > 0 ? { cuentaDolares: cuentaD } : {}),
            ...extras,
          });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  /** What an account holds by the books, as the sheet lists it: a dollar account in its dollars. */
  const enLibros = (cuenta: CuentaBanco) => {
    const s = saldos?.bancos[cuenta];
    const puesto = nombrePuesto(config, cuenta);
    const etiqueta = puesto
      ? t('mover.enCuenta', { cuenta: puesto })
      : t(esCuentaEnDolares(cuenta) ? 'mover.enBancoDolares' : 'mover.enBanco');
    const valor = esCuentaEnDolares(cuenta) ? enDolares(s?.dolares ?? centavos(0)) : formatMoney(s?.cordobas ?? centavos(0));
    return <Dato key={cuenta} etiqueta={etiqueta} valor={valor} />;
  };
  const moneda = (cuenta: CuentaBanco) => (esCuentaEnDolares(cuenta) ? 'USD' : 'NIO');

  return (
    <Modal
      titulo={t('mover.titulo')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={!valido || guardando} onClick={() => void guardar()}>
          <Ico icono="guardar" />
          {t(entre ? 'mover.guardarEntre' : alBanco ? 'mover.guardarAlBanco' : 'mover.guardarDelBanco')}
        </button>
      }
    >
      <div className="flex flex-col gap-4" data-mover-dinero>
        <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('mover.sentido')}>
          {(['alBanco', 'delBanco', 'entreCuentas'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={sentido === s}
              data-mover-sentido={s}
              data-sin-icono="pestana"
              onClick={() => {
                setSentido(s);
                setError(null);
              }}
              className={`min-w-0 flex-1 rounded-lg px-2 py-2 text-sm transition ${sentido === s ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}
            >
              {t(s === 'alBanco' ? 'mover.alBanco' : s === 'delBanco' ? 'mover.delBanco' : 'mover.entreCuentas')}
            </button>
          ))}
        </div>
        {saldos && (
          <div className="flex flex-col gap-1.5 rounded-xl bg-slate-950/40 px-3 py-2.5 ring-1 ring-white/5 ring-inset" data-mover-saldos>
            {!entre && <Dato etiqueta={t('mover.enEfectivo')} valor={formatMoney(saldos.efectivo)} />}
            {abiertas.map((x) => enLibros(x.cuenta))}
          </div>
        )}
        {entre ? (
          destinos.length === 0 ? (
            <p className="text-sm text-slate-400" data-mover-una-cuenta>
              {t('mover.unaSolaCuenta')}
            </p>
          ) : (
            <>
              <Selector
                etiqueta={t('mover.desde')}
                value={desde}
                onChange={(e) => setElegidaDesde(e.target.value as CuentaBanco)}
                data-mover-desde
              >
                {abiertas.map((x) => (
                  <option key={x.cuenta} value={x.cuenta}>
                    {nombre(x.cuenta)} · {x.moneda === 'USD' ? 'US$' : 'C$'}
                  </option>
                ))}
              </Selector>
              <Selector
                etiqueta={t('mover.hacia')}
                value={hacia ?? ''}
                onChange={(e) => setElegidaHacia(e.target.value as CuentaBanco)}
                data-mover-hacia
              >
                {destinos.map((x) => (
                  <option key={x.cuenta} value={x.cuenta}>
                    {nombre(x.cuenta)} · {x.moneda === 'USD' ? 'US$' : 'C$'}
                  </option>
                ))}
              </Selector>
              <Campo
                etiqueta={t(moneda(desde) === 'USD' ? 'mover.saleDolares' : 'mover.saleCordobas')}
                inputMode="decimal"
                className="tabular text-lg"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="0.00"
                autoComplete="off"
                autoFocus
                error={m === null || m < 0 ? t('caja.montoInvalido') : null}
                data-mover-monto
              />
              {cambia && hacia && (
                <Campo
                  etiqueta={t(moneda(hacia) === 'USD' ? 'mover.llegaDolares' : 'mover.llegaCordobas')}
                  inputMode="decimal"
                  className="tabular text-lg"
                  value={recibido}
                  onChange={(e) => setRecibido(e.target.value)}
                  placeholder="0.00"
                  autoComplete="off"
                  error={r === null || r < 0 ? t('caja.montoInvalido') : null}
                  ayuda={
                    tasaDelBanco
                      ? t('mover.tasaDelBanco', { tasa: formatFxRate(fxRate(tasaDelBanco)), oficial: formatFxRate(config.fxTasaOficial) })
                      : t('mover.recibidoAyuda')
                  }
                  data-mover-recibido
                />
              )}
            </>
          )
        ) : (
          <>
            <Campo
              etiqueta={t('deposito.cordobas')}
              inputMode="decimal"
              className="tabular text-lg"
              value={cordobas}
              onChange={(e) => setCordobas(e.target.value)}
              placeholder="0.00"
              autoComplete="off"
              autoFocus
              error={c === null || c < 0 ? t('caja.montoInvalido') : null}
              {...(hayVariasC ? {} : { ayuda: t(alBanco ? 'deposito.cordobasAyuda' : 'mover.cordobasDelBanco') })}
            />
            <ElegirCuenta
              valor={cuentaC}
              onCambiar={(x) => setElegidaC(x as CuentaBancoCordobas)}
              etiqueta={t(alBanco ? 'mover.cordobasA' : 'mover.cordobasDe')}
            />
            <Campo
              etiqueta={t('deposito.dolares')}
              inputMode="decimal"
              className="tabular text-lg"
              value={dolares}
              onChange={(e) => setDolares(e.target.value)}
              placeholder="0.00"
              autoComplete="off"
              error={d === null || d < 0 ? t('caja.montoInvalido') : null}
              {...(hayVariasD ? {} : { ayuda: t(alBanco ? 'deposito.dolaresAyuda' : 'mover.dolaresDelBanco') })}
            />
            <ElegirCuenta
              soloDolares
              valor={cuentaD}
              onCambiar={(x) => setElegidaD(x as CuentaBancoDolares)}
              etiqueta={t(alBanco ? 'mover.dolaresA' : 'mover.dolaresDe')}
            />
          </>
        )}
        <Campo
          etiqueta={t(entre ? 'mover.referenciaEntre' : alBanco ? 'deposito.numeroBoleta' : 'mover.referenciaDelBanco')}
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
          maxLength={80}
          autoComplete="off"
        />
        <Campo
          etiqueta={t('mover.fecha')}
          type="date"
          value={fecha}
          max={hoy()}
          onChange={(e) => setDia(e.target.value)}
          error={fechaValida ? null : t('gastoPaso.fechaFutura')}
        />
        <Campo etiqueta={t('mover.nota')} value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300} autoComplete="off" />
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <p className="text-xs text-slate-500">
          {t(entre ? 'mover.explicacionEntre' : alBanco ? 'mover.explicacionAlBanco' : 'mover.explicacionDelBanco')}
        </p>
      </div>
    </Modal>
  );
}
